import { Button } from "@/components/ui/button";
import { ChevronDown } from "lucide-react";
import heroBackground from "@/assets/hero-background.png";
import bookMockup from "@/assets/book-mockup.png";

const Hero = () => {
  const scrollToPreview = () => {
    document.getElementById("preview")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background with book cover and sensual overlay */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${heroBackground})`,
          backgroundSize: "cover",
          backgroundPosition: "center -100px",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Dark sensual overlay with burgundy hint */}
        <div className="absolute inset-0 bg-[hsl(var(--background))] opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--burgundy))]/20 via-background/80 to-transparent" />
        
        {/* Noise texture */}
        <div className="absolute inset-0 bg-noise opacity-40" />
        
        {/* Ambient glow effects */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-[150px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[hsl(var(--burgundy))]/10 rounded-full blur-[150px] animate-pulse" style={{ animationDelay: "1s" }} />
      </div>
      
      {/* Smooth transition to next section */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-b from-transparent to-background z-[1]" 
        style={{ 
          contain: 'layout style paint',
          willChange: 'transform',
          transform: 'translateZ(0)'
        }} 
      />

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 text-center animate-fade-in pt-[200px]">
        <h1 className="text-5xl md:text-7xl lg:text-8xl font-cinzel font-black text-foreground mb-8 tracking-wider leading-tight">
          Everything That
          <br />
          <span className="text-primary">
            Made Me Yours
          </span>
        </h1>
        
        <p className="text-2xl md:text-3xl text-foreground/90 mb-3 font-playfair italic tracking-wide">
          She doesn't remember him.
        </p>
        
        <p className="text-2xl md:text-3xl text-primary mb-4 font-playfair italic tracking-wide drop-shadow-[0_0_20px_hsl(var(--primary)/0.5)]">
          But her body does.
        </p>
        
        <p className="text-lg text-muted-foreground mb-12 font-lato font-light tracking-wider">
          by C. Venerus
        </p>

        <Button 
          size="lg" 
          onClick={scrollToPreview}
          className="bg-primary hover:bg-primary/80 text-primary-foreground font-lato font-bold text-lg px-12 py-7 rounded-none shadow-[0_0_40px_hsl(var(--primary)/0.6)] hover:shadow-[0_0_60px_hsl(var(--primary)/0.9)] transition-all duration-500 tracking-wider uppercase border border-primary/50"
        >
          I Want a Taste
        </Button>
        
        {/* Book Mockup - directly after button */}
        <div className="mt-16 max-w-3xl mx-auto">
          <div className="relative opacity-90">
            <img 
              src={bookMockup} 
              alt="Available on all devices" 
              width="1536"
              height="1024"
              fetchPriority="high"
              className="w-full h-auto relative z-10 drop-shadow-[0_15px_50px_rgba(0,0,0,0.7)]"
              style={{
                filter: "brightness(0.9) contrast(1.05)",
              }}
            />
            <div className="absolute top-[35%] left-[50%] -translate-x-1/2 w-[300px] h-[150px] bg-primary/10 rounded-full blur-[60px]" />
          </div>
          <p className="text-center mt-6 text-lg md:text-xl font-playfair italic text-primary/70">
            Available in all formats
          </p>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={scrollToPreview}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-bounce"
        aria-label="Scroll down"
      >
        <ChevronDown className="w-8 h-8 text-primary drop-shadow-[0_0_10px_hsl(var(--primary))]" />
      </button>
    </section>
  );
};

export default Hero;
